import { ClickAwayListener } from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';
import { handleKeyEvent } from '../../../util/events.util';
import { DropdownPortal } from './DropdownPortal';

interface DropdownProps {
  children: React.ReactNode;
  dropdown: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  externalUpdate?: string;
  maxHeight?: number | null;
  width?: string | number | null;
  leftOffset?: number;
  className?: string;

  additionalOffsetTop?: number;
}

export default function Dropdown({
  children,
  dropdown,
  open,
  setOpen,
  externalUpdate,
  maxHeight: maxDropdownHeight = 200,
  width = null,
  leftOffset = 0,
  className = '',
  additionalOffsetTop = 0,
}: DropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [renderCheck, setRenderCheck] = useState(false);
  const [maxHeight, _setMaxHeight] = useState<number | undefined>(undefined);
  const setMaxHeight = (height: number) => {
    if (maxDropdownHeight) _setMaxHeight(height > maxDropdownHeight ? maxDropdownHeight : height);
    else _setMaxHeight(height);
  };
  const [position, setPosition] = useState<'top' | 'bottom'>('top');
  const [mouseDown, setMouseDown] = useState(false);
  const [containerRect, setContainerRect] = useState({ left: 0, top: 0, width: 0, height: 0 });

  const handlePositionUpdate = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setContainerRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });

    if (!dropdownRef.current) return;
    if (!renderCheck) setRenderCheck(true);

    const dropdownHeight = dropdownRef.current.clientHeight;

    const topPosition = rect.top - 4 - dropdownHeight;
    const bottomPosition = rect.bottom + 4 + dropdownHeight;

    const spaceTop = topPosition;
    const spaceBottom = window.innerHeight - bottomPosition;

    if (spaceBottom >= 8 || spaceBottom >= spaceTop) {
      setMaxHeight(spaceBottom + dropdownHeight - 8);
      setPosition('bottom');
    } else {
      setMaxHeight(spaceTop + dropdownHeight - 8);
      setPosition('top');
    }
  };

  useEffect(() => {
    const ac = new AbortController();

    let timeoutId: NodeJS.Timeout;

    const debouncedHandlePositionUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => handlePositionUpdate(), 100);
    };

    window.addEventListener('resize', debouncedHandlePositionUpdate, ac);

    return () => {
      clearTimeout(timeoutId);
      ac.abort();
    };
  }, []);

  useEffect(() => {
    const ac = new AbortController();

    window.addEventListener('keydown', event => handleKeyEvent(event, 'Escape', event => setOpen(false)));

    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (!open) setRenderCheck(false);
  }, [open]);

  useEffect(() => {
    handlePositionUpdate();
  }, [renderCheck, open, externalUpdate, dropdown]);

  const handleBlur = (event: React.FocusEvent) => {
    if (!mouseDown) setOpen(false);
  };

  return (
    <ClickAwayListener mouseEvent='onMouseDown' touchEvent='onTouchStart' onClickAway={() => setOpen(false)}>
      <div ref={containerRef} className={`${className} relative h-fit leading-none`} onBlur={handleBlur}>
        {children}
        {open && (
          <DropdownPortal>
            <div
              onClick={event => event.stopPropagation()}
              onMouseDownCapture={() => setMouseDown(true)}
              onMouseUp={() => setMouseDown(false)}
              ref={dropdownRef}
              className={`fixed overflow-hidden rounded-2 bg-white py-2 shadow-default`}
              style={{
                maxHeight,
                visibility: renderCheck ? 'visible' : 'hidden',
                zIndex: renderCheck ? 40 : -40,
                left: containerRect.left + leftOffset,
                top: containerRect.top + additionalOffsetTop,
                width: width || containerRect.width,
                transform: `translate(0, ${
                  position === 'top' ? 'calc(-100% - 4px)' : containerRect.height + 4 + 'px'
                })`,
              }}
            >
              {dropdown}
            </div>
          </DropdownPortal>
        )}
      </div>
    </ClickAwayListener>
  );
}
